import {
    useCallback,
    useMemo, useState,
} from 'react'

import useFieldsWithClassName from 'app/hooks/useFieldsWithClassName'
import DomainObjectForm from 'app/shared-components/DomainObjectForm'
import Button from 'app/shared-components/Button'
import ButtonContainer from 'app/shared-components/ButtonContainer'
import useEnumValues from 'app/hooks/useEnumValues'
import StatusHandler from 'app/shared-components/StatusHandler'
import ConfirmationDialog from 'app/shared-components/ConfirmationDialog'
import useOrderManagementRoutes from 'app/SkyNetRoutes/hooks/useOrderManagementRoutes'
import {
    StatusLongTermLease,
} from 'app/enums/label.enums'

import useGetLongTermLease from '../hooks/useGetLongTermLease'
import LongTermLeaseComments from '../LongTermLeaseComments'
import useUpdateStatus from '../hooks/useUpdateStatus'
import getFields from './longTermLeaseOverview.fields'
import useStyles from './LongTermLeaseOverview.styles'

const DIALOG_CONTENT = 'Are you sure that you want to close this contract? Once confirmed, linked Containers will no longer be reserved for this client’s orders.'

const LongTermLeaseOverview = ({
    id,
}: {
    id: number
}) => {
    const {
        classes,
    } = useStyles()
    const getAllowedValues = useEnumValues()
    const {
        LongTermLease: LongTermLeaseRoutes,
    } = useOrderManagementRoutes()

    const [
        openConfirmationDialog,
        setOpenConfirmationDialog,
    ] = useState(false)

    const {
        data,
        isDataReady,
        isFetching,
        isError,
        error,
        invalidate,
    } = useGetLongTermLease(id)

    const onSuccess = useCallback(() => {
        setOpenConfirmationDialog(false)
        invalidate()
    }, [invalidate])

    const {
        mutate,
        isPending,
    } = useUpdateStatus(onSuccess)

    const allowedProductTypes = useMemo(() => {
        return data?.requiredContainers?.map(({
            productType,
        }) => {
            return productType
        })
    }, [data?.requiredContainers])

    const fields = useFieldsWithClassName(getFields({
        getAllowedValues,
        allowedProductTypes,
        status: data?.status,
    }), classes)

    const onChangeStatus = (action: StatusLongTermLease) => {
        mutate({
            id,
            action: action.toLowerCase(),
        })
    }

    const onClickCloseContract = () => {
        setOpenConfirmationDialog(true)
    }

    const onChangeStatusClosed = (action: boolean) => {
        if (!action) {
            setOpenConfirmationDialog(false)

            return
        }

        onChangeStatus(StatusLongTermLease.CLOSED)
    }

    const moveToContainersTab = () => {
        LongTermLeaseRoutes.Containers.navigate({
            params: {
                key: id,
            },
        })
    }

    return (
        <StatusHandler
            isSuccess={isDataReady}
            isFetching={isFetching}
            isError={isError}
            error={error}
        >
            <>
                <DomainObjectForm
                    value={data}
                    className={classes.gridWrapper}
                    fields={fields}
                    disabled
                    name="LongTermLease"
                />
                <ButtonContainer>
                    {data?.status === StatusLongTermLease.NOT_STARTED && (
                        <Button
                            label="confirm"
                            onClick={() => {
                                onChangeStatus(StatusLongTermLease.CONFIRMED)
                            }}
                            saving={isPending}
                            data-testid="confirm-button"
                        />
                    )}
                    {data?.status === StatusLongTermLease.CONFIRMED && (
                        <Button
                            label="add containers"
                            onClick={moveToContainersTab}
                        />
                    )}
                    {data?.status === StatusLongTermLease.BOOKED && (
                        <Button
                            label="start rental"
                            onClick={() => {
                                onChangeStatus(StatusLongTermLease.RENTAL)
                            }}
                            saving={isPending}
                            data-testid="rental-button"
                        />
                    )}
                    {data?.status === StatusLongTermLease.RENTAL && (
                        <Button
                            label="close contract"
                            onClick={onClickCloseContract}
                            data-testid="closed-button"
                        />
                    )}
                </ButtonContainer>
                <LongTermLeaseComments longTermLeaseId={id} />
                <ConfirmationDialog
                    open={openConfirmationDialog}
                    handleClose={onChangeStatusClosed}
                    dialogTitle="CLOSE CONTRACT"
                    dialogContent={DIALOG_CONTENT}
                    positiveLabel="confirm"
                    negativeLabel="cancel"
                    isLoading={isPending}
                />
            </>
        </StatusHandler>
    )
}

export default LongTermLeaseOverview
